import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, money, timeOf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Photo } from "@/components/Photo";
import { toast } from "sonner";
import {
  Baby, Printer, WarningCircle, CheckCircle, Clock, CalendarDots, CurrencyDollar,
  ChatCircleDots, ArrowRight, ClipboardText, Camera,
} from "@phosphor-icons/react";

const STATUS_STYLE = {
  in: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-[#10B981]", icon: CheckCircle },
  out: { chip: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-[#3B82F6]", icon: CheckCircle },
  absent: { chip: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-[#FBBF24]", icon: WarningCircle },
  none: { chip: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400", icon: Clock },
};

const ChildCard = ({ k, day }) => {
  const s = STATUS_STYLE[k.status] || STATUS_STYLE.none;
  const Icon = s.icon;
  return (
    <div className="bg-white rounded-3xl border border-border p-6 flex flex-col" data-testid={`child-card-${k.id}`}>
      <div className="flex items-start gap-4">
        {k.photo_path ? (
          <Photo path={k.photo_path} alt={k.name} className="h-14 w-14 rounded-2xl object-cover shrink-0"
                 testid={`child-photo-${k.id}`} />
        ) : (
          <span className="h-14 w-14 rounded-2xl bg-blue-50 text-[#3B82F6] grid place-items-center shrink-0">
            <Baby size={28} weight="duotone" />
          </span>
        )}
        <div className="min-w-0">
          <p className="font-display text-lg font-semibold truncate">{k.preferred_name || k.first_name}</p>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3B82F6] mt-1 truncate">
            {k.room || "No classroom yet"}
          </p>
        </div>
      </div>

      <div className={`mt-5 inline-flex items-center gap-2 self-start rounded-full border px-3 py-1.5 text-sm font-bold ${s.chip}`}
           data-testid={`child-status-${k.id}`}>
        <span className={`h-2 w-2 rounded-full ${s.dot}`} />
        <Icon size={16} weight="bold" /> {k.status_label}
      </div>

      <dl className="mt-5 space-y-2 text-sm">
        {k.arrival_at && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground font-semibold">Signed in</dt>
            <dd className="font-semibold" data-testid={`child-arrival-${k.id}`}>
              {timeOf(k.arrival_at)}{k.guardian ? ` · ${k.guardian}` : ""}
            </dd>
          </div>
        )}
        {k.departure_at && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground font-semibold">Signed out</dt>
            <dd className="font-semibold">{timeOf(k.departure_at)}</dd>
          </div>
        )}
        {k.status === "absent" && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground font-semibold">Reason</dt>
            <dd className="font-semibold">{k.absence_reason || "Not given"}</dd>
          </div>
        )}
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground font-semibold">Logged today</dt>
          <dd className="font-semibold" data-testid={`child-activity-count-${k.id}`}>
            {k.activity_total === 0 ? "Nothing yet" : `${k.activity_total} update${k.activity_total === 1 ? "" : "s"}`}
          </dd>
        </div>
        {k.open_requests > 0 && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground font-semibold">Open requests</dt>
            <dd className="font-semibold text-[#3B82F6]">{k.open_requests}</dd>
          </div>
        )}
      </dl>

      <div className="mt-auto pt-5 flex flex-wrap gap-2">
        <Link to={`/portal/daysheet/${k.id}?day=${day}`} className="flex-1 min-w-[9rem]">
          <Button size="sm" variant="outline" className="rounded-full font-bold w-full"
                  data-testid={`view-daysheet-${k.id}`}>
            <Printer size={15} className="mr-2" /> Day sheet
          </Button>
        </Link>
        <Link to={`/portal/requests?child=${k.id}`} className="flex-1 min-w-[9rem]">
          <Button size="sm" className="rounded-full font-bold w-full bg-[#0F172A] hover:bg-[#1e293b]"
                  data-testid={`request-for-${k.id}`}>
            Ask for something
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default function ParentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/portal/dashboard");
      setData(d);
    } catch (e) {
      setErr(apiError(e.response?.data?.detail));
      toast.error(apiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (err) return <p className="text-sm font-semibold text-[#EF4444]" data-testid="dashboard-error">{err}</p>;
  if (!data) return <p className="text-muted-foreground" data-testid="dashboard-loading">Loading your family portal…</p>;

  const high = data.action_needed.filter((a) => a.severity === "high");
  const rest = data.action_needed.filter((a) => a.severity !== "high");

  return (
    <div data-testid="parent-dashboard">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Family Portal</p>
      <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">
        Hi {user?.name?.split(" ")[0] || "there"}!
      </h1>
      <p className="mt-3 text-muted-foreground">
        {new Date(`${data.day}T12:00:00`).toLocaleDateString("en-US",
          { weekday: "long", month: "long", day: "numeric" })}
      </p>

      {data.no_children && (
        <div className="mt-8 bg-white rounded-3xl border border-border p-6" data-testid="no-children-notice">
          <p className="font-display text-xl font-semibold">No children connected yet</p>
          <p className="mt-3 text-sm text-muted-foreground">
            Your account is set up, but nobody has been connected to it. The office connects your child once
            enrollment is confirmed — call <strong>(501) 904-7103</strong> or text (501) 791-1919 and we will
            sort it out. Until then there is nothing here to see.
          </p>
        </div>
      )}

      {data.action_needed.length > 0 && (
        <section className="mt-8 rounded-3xl border-2 border-[#FBBF24] bg-amber-50/60 p-6" data-testid="action-needed">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-2xl bg-[#FBBF24] text-[#0F172A] grid place-items-center shrink-0">
              <WarningCircle size={22} weight="bold" />
            </span>
            <div>
              <h2 className="text-base md:text-lg font-bold">Action needed</h2>
              <p className="text-xs text-muted-foreground font-semibold">
                {data.action_needed.length} thing{data.action_needed.length === 1 ? "" : "s"} waiting on you
              </p>
            </div>
          </div>
          <ul className="mt-5 space-y-2">
            {[...high, ...rest].map((a) => (
              <li key={a.key}>
                <Link to={a.link} data-testid={`action-${a.kind}`}
                      className="flex items-center gap-4 bg-white rounded-2xl border border-border px-4 py-3 hover:border-[#3B82F6]"
                      style={{ transition: "border-color .2s ease, transform .2s ease" }}>
                  <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${a.severity === "high" ? "bg-[#EF4444]" : "bg-[#FBBF24]"}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold truncate">{a.label}</span>
                    {a.detail && <span className="block text-xs text-muted-foreground truncate">{a.detail}</span>}
                  </span>
                  <ArrowRight size={16} className="shrink-0 text-[#3B82F6]" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="my-children">
        {data.children.map((k) => <ChildCard key={k.id} k={k} day={data.day} />)}
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-3xl border border-border p-6 lg:col-span-2" data-testid="today-feed">
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-display text-xl font-semibold">Today at a glance</h2>
            <Link to="/portal/history" className="text-sm font-bold text-[#3B82F6]" data-testid="to-history-link">
              History
            </Link>
          </div>
          <div className="mt-5 space-y-4">
            {data.today.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="today-empty">
                Nothing has been logged yet today. Updates appear here as the classroom records them.
              </p>
            )}
            {data.today.map((a) => (
              <div key={a.id} className="border-b border-border pb-3 last:border-0">
                <p className="text-sm font-bold">
                  {a.student_name} · <span className="capitalize text-[#3B82F6]">{a.type}</span>
                  {a.source === "parent_reported" && (
                    <span className="ml-2 text-xs font-bold uppercase tracking-[0.12em] text-amber-700">
                      Parent-reported
                    </span>
                  )}
                </p>
                {a.photo_path && (
                  <Photo path={a.photo_path} alt={a.detail || "Photo from today"}
                         className="mt-2 rounded-2xl w-full h-56 object-cover"
                         testid={`parent-photo-${a.id}`} />
                )}
                {a.detail && <p className="text-sm text-muted-foreground mt-1">{a.detail}</p>}
                <p className="text-xs text-muted-foreground mt-1">{timeOf(a.at)} · {a.staff}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-[#0F172A] text-white rounded-3xl p-6" data-testid="balance-card">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/60">Balance owed</p>
            <p className="mt-3 font-display text-4xl font-bold" data-testid="balance-amount">
              {money(data.balance.total)}
            </p>
            {data.balance.overdue > 0 && (
              <p className="mt-2 text-sm font-bold text-[#FBBF24]" data-testid="balance-overdue">
                {money(data.balance.overdue)} past due
              </p>
            )}
            <Link to="/portal/billing">
              <Button className="mt-5 w-full rounded-full font-bold bg-[#3B82F6] hover:bg-[#2563EB]"
                      data-testid="pay-now-btn">
                <CurrencyDollar size={17} className="mr-2" />
                {data.balance.total > 0 ? "Make a payment" : "View tuition"}
              </Button>
            </Link>
          </div>

          <div className="bg-white rounded-3xl border border-border p-6" data-testid="quick-links">
            <h2 className="font-display text-lg font-semibold">Quick actions</h2>
            <div className="mt-4 space-y-2">
              {[
                { to: "/portal/requests", label: "Make a request", icon: ClipboardText, testid: "quick-requests" },
                { to: "/portal/messages", label: `Messages${data.unread_messages ? ` (${data.unread_messages})` : ""}`, icon: ChatCircleDots, testid: "quick-messages" },
                { to: "/portal/album", label: "Photo album", icon: Camera, testid: "quick-album" },
                { to: "/portal/calendar", label: "Calendar", icon: CalendarDots, testid: "quick-calendar" },
              ].map((l) => (
                <Link key={l.to} to={l.to} data-testid={l.testid}
                      className="flex items-center gap-3 rounded-2xl border border-border px-4 py-3 text-sm font-bold hover:border-[#3B82F6]"
                      style={{ transition: "border-color .2s ease" }}>
                  <l.icon size={18} weight="duotone" className="text-[#3B82F6]" /> {l.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-border p-6" data-testid="upcoming-events">
            <h2 className="font-display text-lg font-semibold">Coming up</h2>
            <div className="mt-4 space-y-3">
              {data.events.length === 0 && (
                <p className="text-sm text-muted-foreground">Nothing on the calendar for the next month.</p>
              )}
              {data.events.map((e) => (
                <div key={e.id} className="text-sm">
                  <p className="font-bold">{e.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(`${e.date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    {e.room ? ` · ${e.room}` : ""}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
